'use client';

import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext, useState } from 'react';
import Countdown from 'react-countdown';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useTelephoneVerification } from '@/features/auth/lib/useTelephoneVerification';
import { EventService } from '@/shared/lib/event-service';
import { getFormatedPhoneNumber } from '@/shared/lib/getFormatedPhoneNumber';
import { Button } from '@/shared/ui/button';
import { Form } from '@/shared/ui/form';
import { Layout } from '@/shared/ui/layout';
import { ModalProps } from '@/shared/ui/modal/types';
import './verify-phone-number-modal.scss';

const CODE_LENGTH = 4;

type FormValues = {
    code: string;
};

type Props = ModalProps & {
    phoneNumber: string;
    nextRetryAt?: string;
    children?: ReactNode;
    onVerify?(): void;
    fallbackToLocal?: boolean;
};

type VerifyPhoneNumberModalContextValue = {
    form: UseFormReturn<FormValues>;
    phoneNumber: string;
    modalId: number;
    date: number;
    setDate: (date: number) => void;
    onClose: ModalProps['onClose'];
    onVerify?(): void;
    handleSubmit(values: FormValues): Promise<void>;
    startVerification: ReturnType<typeof useTelephoneVerification>['startVerification'];
    finishVerification: ReturnType<typeof useTelephoneVerification>['finishVerification'];
};

const VerifyPhoneNumberModalContext = createContext<VerifyPhoneNumberModalContextValue | null>(null);

const useVerifyPhoneNumberModalContext = () => {
    const context = useContext(VerifyPhoneNumberModalContext);
    if (!context) {
        throw new Error('VerifyPhoneNumberModal components must be used within VerifyPhoneNumberModal.');
    }
    return context;
};

const VerifyPhoneNumberModalProvider = ({
    children,
    phoneNumber,
    nextRetryAt,
    modalId,
    onVerify,
    fallbackToLocal,
    ...props
}: Props) => {
    const form = useForm<FormValues>({
        defaultValues: {
            code: '',
        },
    });

    const [date, setDate] = useState(() => (nextRetryAt ? new Date(nextRetryAt).getTime() : Date.now()));
    const { startVerification, finishVerification } = useTelephoneVerification(phoneNumber, {
        fallbackToLocal,
    });

    const t = useTranslations();
    const handleSubmit = async ({ code }: FormValues) => {
        try {
            await finishVerification.mutateAsync(code);
            onVerify?.();
            EventService.trigger('phone-verification:finish', { code, modalId });
        } catch {
            EventService.trigger('notifications:show', {
                type: 'error',
                title: t('VERIFY_PHONE_NUMBER_MODAL.NOTIFICATIONS.WRONG_CODE_TITLE'),
                description: t('VERIFY_PHONE_NUMBER_MODAL.NOTIFICATIONS.WRONG_CODE_DESCRIPTION'),
            });
        }
    };

    return (
        <VerifyPhoneNumberModalContext.Provider
            value={{
                form,
                phoneNumber,
                modalId,
                date,
                setDate,
                onClose: props.onClose,
                onVerify,
                handleSubmit,
                startVerification,
                finishVerification,
            }}
        >
            {children}
        </VerifyPhoneNumberModalContext.Provider>
    );
};

const VerifyPhoneNumberModalTitle = () => {
    const t = useTranslations();
    return t('VERIFY_PHONE_NUMBER_MODAL.TITLE');
};

type DescriptionProps = ComponentProps<typeof Layout>;
const VerifyPhoneNumberModalDescription = ({ media, ...props }: DescriptionProps) => {
    const t = useTranslations();
    const { phoneNumber } = useVerifyPhoneNumberModalContext();
    return (
        <Layout element="p" media={media} {...props}>
            {t('VERIFY_PHONE_NUMBER_MODAL.DESCRIPTION')} <br />
            <span>{getFormatedPhoneNumber(phoneNumber).formated}</span>
        </Layout>
    );
};

type FormProps = ComponentProps<typeof Layout>;
const VerifyPhoneNumberModalForm = ({ ...props }: FormProps) => {
    const { form, handleSubmit } = useVerifyPhoneNumberModalContext();
    const t = useTranslations();
    return (
        <Layout element={Form} {...form} onSubmit={form.handleSubmit(handleSubmit)} {...props} noValidate>
            <Form.TextField
                name="code"
                label={t('VERIFY_PHONE_NUMBER_MODAL.CODE_LABEL')}
                rules={{
                    required: true,
                    minLength: CODE_LENGTH,
                    maxLength: CODE_LENGTH,
                }}
            />
        </Layout>
    );
};

const VerifyPhoneNumberModalCodeValidity = () => {
    const t = useTranslations();
    const { date, setDate, startVerification, phoneNumber } = useVerifyPhoneNumberModalContext();

    const handleResendCode = async () => {
        try {
            const response = await startVerification.mutateAsync(phoneNumber);
            const newDate = response.nextRetryAt ? new Date(response.nextRetryAt).getTime() : Date.now();
            setDate(newDate);
        } catch {
            EventService.trigger('notifications:show', {
                type: 'error',
                title: t('VERIFY_PHONE_NUMBER_MODAL.NOTIFICATIONS.RESEND_FAILED_TITLE'),
                description: t('VERIFY_PHONE_NUMBER_MODAL.NOTIFICATIONS.RESEND_FAILED_DESCRIPTION'),
            });
        }
    };

    return (
        <p className="verify-phone-number-modal__code-validity">
            {`${t('VERIFY_PHONE_NUMBER_MODAL.CODE_VALIDITY')} `}
            <Countdown
                date={date}
                renderer={({ days, hours, minutes, seconds, completed }) => {
                    const totalSeconds = days * 86400 + hours * 3600 + minutes * 60 + seconds;
                    const canResend = completed && !startVerification.isPending;
                    return (
                        <button
                            type="button"
                            disabled={!canResend}
                            data-testid="verify-phone-number-modal-resend"
                            className="verify-phone-number-modal__resend"
                            onClick={handleResendCode}
                        >
                            {completed
                                ? t('VERIFY_PHONE_NUMBER_MODAL.RESEND_BUTTON')
                                : t('VERIFY_PHONE_NUMBER_MODAL.RESEND_BUTTON_COUNTDOWN', {
                                      seconds: totalSeconds,
                                  })}
                        </button>
                    );
                }}
                key={date}
            />
        </p>
    );
};

type VerifyButtonProps = ComponentProps<typeof Layout>;
const VerifyPhoneNumberModalVerifyButton = ({ media, ...props }: VerifyButtonProps) => {
    const t = useTranslations();
    const { form, finishVerification, handleSubmit } = useVerifyPhoneNumberModalContext();

    return (
        <Layout
            element={Button}
            media={media}
            disabled={finishVerification.isPending}
            data-testid="verify-phone-number-modal-verify-button"
            onClick={form.handleSubmit(handleSubmit)}
            {...props}
        >
            {t('VERIFY_PHONE_NUMBER_MODAL.BUTTON')}
        </Layout>
    );
};

export {
    VerifyPhoneNumberModalProvider as Provider,
    VerifyPhoneNumberModalTitle as Title,
    VerifyPhoneNumberModalDescription as Description,
    VerifyPhoneNumberModalForm as Form,
    VerifyPhoneNumberModalCodeValidity as CodeValidity,
    VerifyPhoneNumberModalVerifyButton as VerifyButton,
};
